import * as React from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import Fade from "@Tmp/Fade";
import { IDialogProps } from ".";
import { useKeyPress } from "@Tmp/Hooks";

const Dialog: React.FC<IDialogProps> = ({
    type,
    open,
    setOpen,
    header,
    footer,
    classes,
    disableCloseOnBackdropClick = false,
    autoHeight,
    Dialog: DialogProps,
    ContainerProps = {},
    ContentProps = {},
    alignFooter,
    ...props
}) => {
    useKeyPress(["Escape"], {
        callback: (pressedKeys) => setOpen(false),
    });
    const handleClose = () => {
        setOpen(false);
    };

    if (typeof window === "undefined") {
        return null;
    }

    React.useLayoutEffect(() => {
        if (open) {
            document.body.classList.add("tmp-dialog-active");
            document.getElementsByTagName("html")?.[0]?.classList.add("tmp-dialog-active");
        } else {
            document.body.classList.remove("tmp-dialog-active");
            document.getElementsByTagName("html")?.[0]?.classList.remove("tmp-dialog-active");
        }
    }, [open]);

    const dialogStyles = {};
    if (!footer) {
        dialogStyles["--dialog-footer-height"] = 0;
    }

    return createPortal(
        <Fade in={open}>
            <div {...ContainerProps} className={clsx(classes?.container, "tmp-dialog__container")}>
                <div
                    {...DialogProps}
                    className={clsx(classes?.dialog, "tmp-dialog", {
                        "tmp-dialog__auto-height": autoHeight,
                        "tmp-dialog__default": type === "default",
                        "tmp-dialog__readable-content": type === "readable-content",
                    })}
                    style={{ ...dialogStyles, ...DialogProps?.style }}
                >
                    <div className="tmp-dialog__header">
                        {header}
                        <div className="tmp-dialog__close" onClick={handleClose}>
                            <i className="fas fa-times fa-2x" />
                        </div>
                    </div>
                    <div
                        {...ContentProps}
                        className={clsx(classes?.content, "tmp-dialog__content")}
                    >
                        {props.children}
                    </div>
                    {footer && (
                        <div
                            className={clsx("tmp-dialog__footer", {
                                "tmp-dialog__footer-right": alignFooter === "right",
                                "tmp-dialog__footer-left": alignFooter === "left",
                                "tmp-dialog__footer-center": alignFooter === "center",
                            })}
                        >
                            {footer}
                        </div>
                    )}
                </div>
                <div
                    className="tmp-dialog__backdrop"
                    onClick={() => {
                        if (disableCloseOnBackdropClick === false) {
                            handleClose();
                        }
                    }}
                ></div>
            </div>
        </Fade>,
        document.body
    );
};

export default Dialog;
